import { Tag as TagDTO } from '@dto/tag.dto';
import {
  FilterTag,
  FilterTagType,
  SelectionType,
  SubsystemClassificationItem,
} from '../search.types';

type SearchFilterTypeLabels = Partial<Record<FilterTagType | SelectionType, string>>;

interface FilterFactoryContext {
  availableArchitecture?: SubsystemClassificationItem[];
  availableTagsCategories?: TagDTO[];
  typeLabels?: SearchFilterTypeLabels;
}

const getClassLabel = (
  availableArchitecture: SubsystemClassificationItem[],
  classValue: string
): string => {
  return (
    availableArchitecture.find((item) => 'class' in item && item.class === classValue)
      ?.classLabel || classValue
  );
};

const getHwTypeLabel = (
  availableArchitecture: SubsystemClassificationItem[],
  hwTypeValue: string
): string => {
  return (
    availableArchitecture.find((item) => 'hwType' in item && item.hwType === hwTypeValue)
      ?.hwTypeLabel || hwTypeValue
  );
};

const getSubsystemLabel = (
  availableArchitecture: SubsystemClassificationItem[],
  search: { classValue?: string; hwTypeValue?: string; subsystemValue: string; }
): string => {
  if (search.classValue) {
    return (
      availableArchitecture.find(
        (item) =>
          'class' in item &&
          item.class === search.classValue &&
          item.subsystem === search.subsystemValue
      )?.subsystemLabel || search.subsystemValue
    );
  }

  if (search.hwTypeValue) {
    return (
      availableArchitecture.find(
        (item) =>
          'hwType' in item &&
          item.hwType === search.hwTypeValue &&
          item.subsystem === search.subsystemValue
      )?.subsystemLabel || search.subsystemValue
    );
  }

  return search.subsystemValue;
};

const getTypeLabel = (type: FilterTagType, labels?: SearchFilterTypeLabels): string => {
  if (!labels) return type;
  return labels[type] ?? type;
};

export const createFilterTag = (
  type: FilterTagType,
  rawValue: string,
  context: FilterFactoryContext = {}
): FilterTag => {
  const availableArchitecture = context.availableArchitecture ?? [];

  if (type === 'class_subsystem') {
    const [classValue, subsystemValue] = rawValue.split(':');
    const classLabel = getClassLabel(availableArchitecture, classValue);
    const subsystemLabel = getSubsystemLabel(availableArchitecture, { classValue, subsystemValue });
    return {
      type,
      value: rawValue,
      label: `Technology: ${classLabel}, Subsystem: ${subsystemLabel}`,
    };
  }

  if (type === 'hw_type_subsystem') {
    const [hwTypeValue, subsystemValue] = rawValue.split(':');
    const hwTypeLabel = getHwTypeLabel(availableArchitecture, hwTypeValue);
    const subsystemLabel = getSubsystemLabel(availableArchitecture, { hwTypeValue, subsystemValue });
    return {
      type,
      value: rawValue,
      label: `Technology: ${hwTypeLabel}, Subsystem: ${subsystemLabel}`,
    };
  }

  if (type === 'tag_category_tag') {
    const [categoryValue, tagValue] = rawValue.split(':');
    const categoryLabel = categoryValue;
    const tagLabel = tagValue === '*' ? 'All' : tagValue;
    return {
      type,
      value: rawValue,
      label: `Tag Category: ${categoryLabel}, Tag: ${tagLabel}`,
    };
  }

  return {
    type,
    value: rawValue,
    label: getTypeLabel(type, context.typeLabels),
  };
};

